<template>
  <form
    class="code-entry-form-ui"
    autocomplete="off"
    @submit.prevent
  >
    <LoaderUI
      v-if="showLoader || showLoaderResendSms"
      :modify="showLoader ? 'absolute' : ''"
      class="bg-white"
    />

    <GroupInputCodeUI
      v-if="!showLoaderResendSms"
      @update-code="updateCode"
    />

    <div
      v-if="textServerErrors"
      class="server-error-ui txt-center"
    >
      {{ textServerErrors }}
    </div>

    <div class="code-entry-form-ui__footer">
      <div
        v-if="resendTime"
        class="code-entry-form-ui__resend-time txt-s txt-disabled txt-center"
      >
        {{ btnTitle }}

        <countdown
          :time="resendTime"
          :transform="transformCountdown"
          @end="resendTime = null"
        >
          <template slot-scope="props">
            ({{ props.minutes }}:{{ props.seconds }})
          </template>
        </countdown>
      </div>

      <template v-else>
        <button
          type="button"
          class="code-entry-form-ui__btn btn-ui btn-ui--full btn-ui--secondary"
          :disabled="processExecute"
          @click="sendNewCode"
        >
          {{ btnTitle }}
          <LoaderUI
            v-if="processExecute"
            modify="xxs"
            class="code-entry-form-ui__loader"
          />
        </button>
      </template>

      <CaptchaUI
        ref="recaptcha"
        @verify="submitForResendCode"
        @expired="onCaptchaExpired"
        @render="onRenderRecaptcha"
        @process-execute="setProcessExecute"
      />
    </div>
  </form>
</template>

<script>
import Countdown from '@chenfengyuan/vue-countdown';
import { getServerErrors } from '../../../../utils/validate';
import GroupInputCodeUI from './GroupInputCodeUI.vue';
import LoaderUI from '../LoaderUI.vue';
import CaptchaUI from '../CaptchaUI.vue';

const DEFAULT_RESEND_TIME = 60 * 1000;

export default {
  name: 'CodeEntryFormUI',

  components: {
    CaptchaUI,
    LoaderUI,
    Countdown,
    GroupInputCodeUI
  },

  props: {
    showLoader: {
      type    : Boolean,
      default : false
    },
    serverErrors: {
      type    : [String, Object, Array],
      default : null
    },
    initSendCode: {
      type    : Boolean,
      default : false
    },
    functionForResendCode: {
      type     : Function,
      required : true
    },
    btnTitle: {
      type    : String,
      default : 'Отправить еще код'
    }
  },

  data() {
    return {
      code    : null,
      isValid : true,

      resendTime              : DEFAULT_RESEND_TIME,
      serverErrorForResendSms : '',

      showLoaderResendSms   : true,
      serverErrorsResendSms : null,
      numberTries           : 0,

      coefficient    : 2,
      processExecute : false
    };
  },

  computed: {
    textServerErrors() {
      const errors = this.serverErrorsResendSms ? this.serverErrorsResendSms : this.serverErrors;
      return errors && typeof errors === 'string' ? errors : null;
    }
  },

  methods: {
    onCaptchaExpired() {
      this.$refs.recaptcha.reset();
    },

    sendNewCode() {
      this.$emit('send-new-code');
      this.$refs.recaptcha.execute();
    },

    transformCountdown(props) {
      const newProps = { ...props };

      Object.entries(newProps).forEach(([key, value]) => {
        newProps[key] = value < 10 ? `0${value}` : value;
      });

      return newProps;
    },

    // Повторная отправка кода (смс или звонок)
    async submitForResendCode(recaptchaToken) {
      try {
        this.showLoaderResendSms = true;

        this.$emit('submit-for-resend-code');
        this.resendTime = this.coefficient * DEFAULT_RESEND_TIME;
        this.serverErrorForResendSms = '';

        // После первой отправки кода - меняем значение коэффициента
        if (this.initSendCode) {
          this.coefficient = 2;
        }

        const response = await this.functionForResendCode(recaptchaToken);

        if (response) {
          this.onCaptchaExpired();
          this.serverErrorsResendSms = null;
        }
      } catch (err) {
        this.numberTries += 1;
        const serverErrors = getServerErrors(err);

        // При ошибке токена отправляем форму ещё раз - 3 попытки
        if (serverErrors && serverErrors.token) {
          if (this.numberTries < 3) {
            this.sendNewCode();
          } else {
            this.numberTries = 0;
          }
        }

        this.serverErrorsResendSms = serverErrors && serverErrors.errors ? serverErrors.errors : null;
      } finally {
        this.showLoaderResendSms = false;
      }
    },

    // Метод подтверждения правильности введённого кода
    updateCode(value) {
      this.code = value;

      if (!this.validate()) return;

      setTimeout(() => {
        this.$emit('send-code', value);
      }, 300);
    },

    validate() {
      this.isValid = true;

      if (!this.code || this.code.length !== 4) {
        this.isValid = false;
      }

      return this.isValid;
    },

    onRenderRecaptcha() {
      this.$emit('render-captcha');

      if (this.initSendCode) {
        this.coefficient = 1;
        this.$refs.recaptcha.execute();
      } else {
        this.showLoaderResendSms = false;
      }
    },

    setProcessExecute(value) {
      this.processExecute = value;
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../../../../../css/redesign/utils/grid';

.code-entry-form-ui {
  position: relative;

  &__resend-time {
    margin-top: 16px;

    @include media-breakpoint-up(md) {
      margin-top: 24px;
    }
  }
  &__btn {
    position: relative;
    margin-top: 32px;
    padding-left: 32px;
    padding-right: 32px;
  }

  &__loader.loader {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
  }
}
</style>
